import {createSlice} from '@reduxjs/toolkit';
import {ApiState, createInitialApiState, setApiPending, setApiRejected, setApiSuccess} from '../apiState';
import {createPaddleCustomer, fetchPaddleProducts, fetchSubscriptions} from './subscriptionThunks';
import type {PaddleProducts, Subscription} from '../../domain/types';

interface SubscriptionState {
    api: {
        products: ApiState;
        subscriptions: ApiState;
        customer: ApiState;
    };
    data: {
        products: PaddleProducts | null;
        subscriptions: Subscription[];
        customerId: string | null;
    };
}

const initialState: SubscriptionState = {
  api: {
    products: createInitialApiState(),
    subscriptions: createInitialApiState(),
    customer: createInitialApiState(),
  },
  data: {
    products: null,
    subscriptions: [],
    customerId: null,
  },
};

const subscriptionSlice = createSlice({
  name: 'subscription',
  initialState,
  reducers: {
    // Sign-out clears billing state; nothing here should survive a user switch.
    clearSubscriptionState: () => initialState,
  },
  extraReducers: (builder) => {
    // Billing state belongs to one user — never let it leak across a sign-out.
    builder.addCase('auth/signOut/fulfilled' as any, () => initialState);

    // Products
    builder.addCase(fetchPaddleProducts.pending, (state) => {
      setApiPending(state.api.products);
    });
    builder.addCase(fetchPaddleProducts.fulfilled, (state, action) => {
      state.data.products = action.payload;
      setApiSuccess(state.api.products);
    });
    builder.addCase(fetchPaddleProducts.rejected, (state, action) => {
      setApiRejected(state.api.products, action.payload as string);
    });

    // Subscriptions
    builder.addCase(fetchSubscriptions.pending, (state) => {
      setApiPending(state.api.subscriptions);
    });
    builder.addCase(fetchSubscriptions.fulfilled, (state, action) => {
      state.data.subscriptions = action.payload;
      setApiSuccess(state.api.subscriptions);
    });
    builder.addCase(fetchSubscriptions.rejected, (state, action) => {
      setApiRejected(state.api.subscriptions, action.payload as string);
    });

    // Paddle customer
    builder.addCase(createPaddleCustomer.pending, (state) => {
      setApiPending(state.api.customer);
    });
    builder.addCase(createPaddleCustomer.fulfilled, (state, action) => {
      state.data.customerId = action.payload;
      setApiSuccess(state.api.customer);
    });
    builder.addCase(createPaddleCustomer.rejected, (state, action) => {
      setApiRejected(state.api.customer, action.payload as string);
    });
  },
});

export const {clearSubscriptionState} = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
